import { useState, useEffect, useCallback } from 'react';
import { devApi } from '../api.js';
import { openOverlaps, taskAsPrompt, formatDate } from '../format.js';
import { LIFECYCLE_LABEL, LIFECYCLE_HELP } from '../lifecycle.js';
import { navigate, listPath } from '../OrbitDevelopmentApp.jsx';
import TaskForm from '../components/TaskForm.jsx';
import OverlapNotice from '../components/OverlapNotice.jsx';
import LifecycleControl, { LifecycleBadge } from '../components/LifecycleControl.jsx';

function CopyPromptButton({ task, options, screenshots }) {
  const [state, setState] = useState('idle');   // idle | copied | failed

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(taskAsPrompt(task, options, screenshots));
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      // Clipboard access is refused outside a secure context, and there is no fallback worth
      // building — say so rather than showing "Copied" over nothing.
      setState('failed');
      setTimeout(() => setState('idle'), 3000);
    }
  };

  return (
    <button className="o-btn o-btn-ghost" onClick={copy}>
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Could not copy' : 'Copy as prompt'}
    </button>
  );
}

/**
 * The lifecycle, as a strip above the form: where the task is, when it got there, and the moves
 * this person can make on it.
 *
 * The dates are derived from the transition log rather than stored per status, which is why
 * "Ready for test QA" can be a later date than "Passed test" — a task that was rejected and
 * re-submitted reached both twice, and each cell shows the most recent. The rejection count
 * beside them is what makes that readable rather than confusing.
 */
function LifecycleStrip({ task, options, transitions, onTransition, saving }) {
  const dates = [
    ['Created', task.logged_at || task.created_at],
    ['Ready to start', task.ready_at],
    ['Started', task.started_at],
    ['Ready for test QA', task.ready_for_test_at],
    ['Ready for prod QA', task.ready_for_prod_at],
    ['Completed', task.done_at],
  ].filter(([, v]) => v);

  return (
    <div className="o-panel" style={{ padding: 16, marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <LifecycleBadge status={task.lifecycle_status} />
        <span className="o-note" style={{ margin: 0 }}>
          {LIFECYCLE_HELP[task.lifecycle_status]}
        </span>
        {Number(task.rejection_count) > 0 && (
          <span className="o-badge o-err">
            sent back {task.rejection_count}×
          </span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          <LifecycleControl task={task} options={options} onTransition={onTransition}
            saving={saving} compact />
        </span>
      </div>

      {task.last_rejection_note && task.lifecycle_status === 'in_progress' && (
        <p className="o-hint" style={{ marginTop: 12, marginBottom: 0 }}>
          <b>Sent back from {LIFECYCLE_LABEL[task.last_rejected_from] || 'QA'}
          {task.last_rejected_at ? ` on ${formatDate(task.last_rejected_at)}` : ''}:</b>{' '}
          {task.last_rejection_note}
        </p>
      )}

      {dates.length > 0 && (
        <div style={{
          display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 14, paddingTop: 12,
          borderTop: '1px solid var(--rule-soft)',
        }}>
          {dates.map(([label, value]) => (
            <div key={label}>
              <span className="o-field-lbl">{label}</span>
              <span className="o-num">{formatDate(value)}</span>
            </div>
          ))}
        </div>
      )}

      {transitions.length > 1 && (
        <details style={{ marginTop: 12 }}>
          <summary className="o-note" style={{ cursor: 'pointer', margin: 0 }}>
            History — {transitions.length} entries
          </summary>
          <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
            {transitions.map(tr => (
              <li key={tr.id} className="o-note" style={{ marginBottom: 4 }}>
                <span className="o-num">{formatDate(tr.created_at)}</span>{' '}
                {tr.from_status
                  ? `${LIFECYCLE_LABEL[tr.from_status] || tr.from_status} → `
                  : 'Logged as '}
                <b>{LIFECYCLE_LABEL[tr.to_status] || tr.to_status}</b>
                {tr.actor_name ? ` by ${tr.actor_name}` : ' by the system'}
                {tr.note ? ` — “${tr.note}”` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * Opening a task opens it for editing. There is no read-only view: every field on it was
 * already legible in the form, so the separate page only added a click before anyone could
 * change what they came to change — and a second place for the same values to be laid out.
 *
 * Saving returns to the list, because editing is the errand the task was opened for.
 */
export default function TaskDetailPage({ taskId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [moving, setMoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await devApi.getTask(taskId));
      setErr(null);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await devApi.deleteTask(taskId);
      navigate(listPath());
    } catch (e) { setErr(e.message); }
  };

  // Stays on the page, unlike saving the form. A transition is a small statement about the task
  // — the answer to it is the task, updated, with its new dates and its new set of moves.
  const transition = async (task, toStatus, note) => {
    setMoving(true);
    try {
      const res = await devApi.transitionTask(task.id, toStatus, note);
      setData(s => ({ ...s, task: res.task, transitions: res.transitions }));
      setErr(null);
      return true;
    } catch (e) {
      setErr(e.message);
      return false;
    } finally { setMoving(false); }
  };

  if (loading && !data) return <main className="o-page"><div className="o-state">Loading…</div></main>;
  if (err && !data) return <main className="o-page"><div className="o-state o-error">{err}</div></main>;
  if (!data) return null;

  const { task, options, screenshots = [], transitions = [] } = data;
  const overlaps = openOverlaps(task, data.overlaps);

  return (
    <main className="o-page">
      <button className="o-btn o-btn-ghost o-btn-sm" style={{ marginBottom: 8 }}
        onClick={() => navigate(listPath())}>&larr; All tasks</button>

      {err && <div className="o-state o-error" style={{ textAlign: 'left' }}>{err}</div>}

      <OverlapNotice overlaps={overlaps} />

      <LifecycleStrip task={task} options={options} transitions={transitions}
        onTransition={transition} saving={moving} />

      <TaskForm
        task={task} options={options} attachments={screenshots}
        headerActions={
          <CopyPromptButton task={task} options={options} screenshots={screenshots} />
        }
        onCancel={() => navigate(listPath())}
        onDelete={remove}
        onSaved={() => navigate(listPath())}
      />
    </main>
  );
}
